/**
 * DecisionFlow — OnboardingShell
 * ui/src/shells/OnboardingShell.jsx
 * P0-B Step 5b — S198
 *
 * Shell for one-time tenant onboarding wizard. Route root: /onboarding/
 * Permitted roles: comp_admin, tenant_super_admin
 *
 * Stage sequence (linear, gated):
 *   LOAD      → BootstrapLoadFlow   (LOAD-01 → LOAD-02 → LOAD-03)
 *   CONFIGURE → ConfigureFlowShell  (CONF-01 → CONF-02 → CONF-03 → CONF-04* → CONF-05* → CONF-06)
 *               * Standard+ only
 *   SETUP     → DataSetupFlowShell  (SETUP-01 → … → SETUP-07)
 *   EXECUTE   → ExecuteFlowShell    (EXEC-01 → … → EXEC-06)
 *
 * Navigation contract (ARCH-5):
 *   Inter-stage navigation via CustomEvent('decisionflow:navigate', { detail: { target, source, ...extra } })
 *   Shell owns the listener. Children dispatch. Shell updates currentStage.
 *
 * Auth: cookie-only — credentials: "include" on all fetch calls.
 *   No Bearer token. No authHeader(). Cookie auth only.
 *
 * State ownership (ARCH-1):
 *   Shell owns: currentStage, bootstrapBatchId, aiResponse, aiStatus, tier
 *   Children receive as props, emit changes via callbacks only.
 *   DataSetupFlowShell and ExecuteFlowShell own their own AI state internally —
 *   shell-level aiResponse/aiStatus is not passed into them.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  Circle,
  ChevronRight,
  Upload,
  Settings,
  Sparkles,
  AlertTriangle,
  Database,
  Play,
  ShieldCheck,
} from "lucide-react";

import { useAuth } from "../auth/AuthContext.jsx";
import ThemeButton from "../components/shared/ThemeButton.jsx";
import BootstrapLoadFlow from "../components/tenant-admin/BootstrapLoadFlow.jsx";
import ConfigureFlowShell from "../components/tenant-admin/ConfigureFlowShell.jsx";
import DataSetupFlowShell from "../components/tenant-admin/DataSetupFlowShell.jsx";
import ExecuteFlowShell    from "../components/admin/ExecuteFlowShell.jsx";
import CertificationPanel  from "../shells/CertificationPanel.jsx"; // S200 — Stage 19

// ---------------------------------------------------------------------------
// Stage definitions — linear order, used for progress indicator
// ---------------------------------------------------------------------------

const STAGES = [
  { id: "LOAD",      label: "Load Data",   icon: Upload,       description: "Upload CSV files and validate" },
  { id: "CONFIGURE", label: "Configure",   icon: Settings,     description: "Activate periods and assign roles" },
  { id: "SETUP",     label: "Data Setup",  icon: Database,     description: "Positions, plans, and assignments" },
  { id: "EXECUTE",   label: "Execute",     icon: Play,         description: "Run calculations and export payroll" },
  { id: "CERTIFY",   label: "Certify",     icon: ShieldCheck,  description: "Review period status and certify" },
];

// ---------------------------------------------------------------------------
// Progress indicator — top of shell, shows current stage
// ---------------------------------------------------------------------------

function OnboardingProgress({ currentStage, onStageClick }) {
  return (
    <div className="flex items-center gap-0">
      {STAGES.map((stage, idx) => {
        const stageIdx    = STAGES.findIndex((s) => s.id === currentStage);
        const isComplete  = idx < stageIdx;
        const isActive    = stage.id === currentStage;
        const Icon        = stage.icon;

        return (
          <div key={stage.id} className="flex items-center">
            {/* Step node */}
            <div className="flex flex-col items-center gap-1.5">
              <div
                onClick={() => !isActive && onStageClick(stage.id, isComplete, idx > STAGES.findIndex((s) => s.id === currentStage))}
                className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                  isActive ? "cursor-default" : "cursor-pointer hover:opacity-80"
                } ${
                  isComplete
                    ? "border-[rgb(var(--color-success))] bg-[rgb(var(--color-success))] text-white"
                    : isActive
                      ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))] text-white"
                      : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-muted))]"
                }`}
              >
                {isComplete
                  ? <CheckCircle2 className="h-4 w-4" />
                  : <Icon className="h-4 w-4" />
                }
              </div>
              <div className="text-center">
                <div className={`text-tok-xs font-medium ${
                  isActive
                    ? "text-[rgb(var(--color-text))]"
                    : "text-[rgb(var(--color-text-muted))]"
                }`}>
                  {stage.label}
                </div>
                <div className="hidden text-tok-xs text-[rgb(var(--color-text-muted))] sm:block">
                  {stage.description}
                </div>
              </div>
            </div>

            {/* Connector line between steps */}
            {idx < STAGES.length - 1 && (
              <div
                className={`mx-3 mb-6 h-0.5 w-16 transition-colors sm:w-24 ${
                  idx < STAGES.findIndex((s) => s.id === currentStage)
                    ? "bg-[rgb(var(--color-success))]"
                    : "bg-[rgb(var(--color-border))]"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OnboardingShell root
// ---------------------------------------------------------------------------

export default function OnboardingShell() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const mode = import.meta.env.VITE_APP_MODE === "dev" ? "dev" : "prod";

  // Tier from auth context — drives stage tier branching (ARCH-9, ER-1)
  const tier = user?.subscription_tier ?? "essentials";

  // Current wizard stage
  const [currentStage, setCurrentStage] = useState(
    () => sessionStorage.getItem("onboarding_stage") || "LOAD"
  );

  // Shared state owned by shell (ARCH-1)
  // Note: aiResponse/aiStatus below are used by LOAD and CONFIGURE only.
  // DataSetupFlowShell and ExecuteFlowShell own their AI state internally.
  const [bootstrapBatchId, setBootstrapBatchId] = useState(null);
  const [aiResponse,       setAiResponse]       = useState(null);
  const [aiStatus,         setAiStatus]         = useState("idle"); // idle | loading | ready | error | timeout | blocked

  // OI-S206-01: EXECUTE stage dynamic fiscal period + scenario set
  // Fetched on EXECUTE mount — never hardcoded.
  const [executeFiscalPeriodId,    setExecuteFiscalPeriodId]    = useState(null);
  const [executeFiscalPeriodLabel, setExecuteFiscalPeriodLabel] = useState(null);
  const [executeScenarioSetId,     setExecuteScenarioSetId]     = useState(null);
  const [executeLoadError,         setExecuteLoadError]         = useState(null);
  const executeFetchedRef = useRef(false);

  // OI-S213-02: warning banner when navigating to an incomplete stage via top-bar click
  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);

  // OI-S213-02: top-bar stage click handler
  // isFuture = stage not yet reached — show warning. isComplete = already done — no warning.
  const handleStageClick = useCallback((stageId, isComplete, isFuture) => {
    setShowIncompleteWarning(isFuture);
    setCurrentStage(stageId);
    sessionStorage.setItem("onboarding_stage", stageId);
  }, []);

  // M040 C0: dark/setDark state and useEffect removed — ThemeButton is self-contained.
  // Theme is managed via localStorage key "decisionflow-theme" and data-theme on <html>.

  // OI-S206-01: fetch active fiscal period + scenario set on EXECUTE mount.
  // Runs once per EXECUTE stage entry (ref guard prevents double-fetch on re-render).
  // GET /api/v1/fiscal-periods?is_active=true → pick first result
  // GET /api/v1/scenario-sets?fiscal_period_id=X → pick first result
  useEffect(() => {
    if (currentStage !== "EXECUTE") return;
    if (executeFetchedRef.current) return;
    executeFetchedRef.current = true;

    async function fetchExecuteContext() {
      try {
        const fpRes  = await fetch("/api/v1/fiscal-periods?is_active=true", { credentials: "include" });
        if (!fpRes.ok) throw new Error("Failed to load fiscal periods.");
        const fpData = await fpRes.json();
        const fps    = fpData?.fiscal_periods || fpData?.data || [];
        if (!fps.length) throw new Error("No active fiscal period found.");

        const fp = fps[0];
        setExecuteFiscalPeriodId(fp.fiscal_period_id);
        setExecuteFiscalPeriodLabel(fp.period_label ?? fp.period_code ?? null);

        const ssRes  = await fetch(`/api/v1/scenario-sets?fiscal_period_id=${fp.fiscal_period_id}`, { credentials: "include" });
        if (!ssRes.ok) throw new Error("Failed to load scenario sets.");
        const ssData = await ssRes.json();
        const sets   = Array.isArray(ssData)
          ? ssData
          : ssData?.scenario_sets || ssData?.data || [];
        if (!sets.length) throw new Error("No scenario set found for the active fiscal period.");

        setExecuteScenarioSetId(sets[0].scenario_set_id);
      } catch (err) {
        setExecuteLoadError(err.message ?? "Unable to load EXECUTE context.");
      }
    }

    fetchExecuteContext();
  }, [currentStage]);

  // Children dispatch CustomEvent('decisionflow:navigate', { detail: { target, source, ...extra } })
  // Shell listens and routes accordingly.
  useEffect(() => {
    function handleNavigate(e) {
      const { target, source, bootstrapBatchId: batchId } = e.detail ?? {};

      if (batchId) setBootstrapBatchId(batchId);
      if (target) sessionStorage.setItem("onboarding_stage", target);

      switch (target) {
        case "LOAD":
          setCurrentStage("LOAD");
          break;
        case "CONFIGURE":
          setCurrentStage("CONFIGURE");
          break;
        case "SETUP":
          // CONFIGURE complete → advance to DATA SETUP
          setCurrentStage("SETUP");
          break;
        case "EXECUTE":
          // DATA SETUP complete → advance to EXECUTE
          setCurrentStage("EXECUTE");
          break;
        case "COMPLETE":
          // EXECUTE complete → advance to CERTIFY (Stage 19) — S200
          setCurrentStage("CERTIFY");
          break;
        // case "COMPLETE": navigate("/admin/setup-guide"); — S198 provisional stub, replaced S200
        default:
          break;
      }
    }

    window.addEventListener("decisionflow:navigate", handleNavigate);
    return () => window.removeEventListener("decisionflow:navigate", handleNavigate);
  }, [navigate]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const tenantId   = user?.tenant_id   ?? null;
  const tenantName = user?.tenant_name ?? null;
  const userName   = user?.full_name   ?? user?.email ?? null;

  return (
    <div className="min-h-screen bg-[rgb(var(--color-bg))] text-[rgb(var(--color-text))]">

      {/* Top bar */}
      <div className="sticky top-0 z-40 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-[rgb(var(--color-primary))]" />
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">DecisionFlow Setup</span>
            {tenantName && (
              <span className="hidden text-tok-xs text-[rgb(var(--color-text-muted))] sm:block">· {tenantName}</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {mode === "dev" && (
              <span className="rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-primary))]">
                dev
              </span>
            )}
            {userName && (
              <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{userName}</span>
            )}
            <ThemeButton />
          </div>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
        <div className="mx-auto max-w-5xl px-4 py-6">
          <OnboardingProgress currentStage={currentStage} onStageClick={handleStageClick} />
        </div>
      </div>

      {/* Stage content */}
      <div className="mx-auto max-w-5xl px-4 py-6">
        {/* OI-S213-02: incomplete stage warning — shown on every top-bar click to a non-complete stage, dismissable */}
        {showIncompleteWarning && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-[rgb(var(--color-warning))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>This stage may not be fully complete. Some data or configurations may be missing — review carefully before proceeding.</span>
            </div>
            <button
              onClick={() => setShowIncompleteWarning(false)}
              className="shrink-0 text-[rgb(var(--color-warning))] hover:text-[rgb(var(--color-text))]"
              aria-label="Dismiss warning"
            >
              ✕
            </button>
          </div>
        )}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStage}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
          >
            {currentStage === "LOAD" && (
              <BootstrapLoadFlow
                tenantId={tenantId}
                tier={tier}
                aiResponse={aiResponse}
                aiStatus={aiStatus}
                onAiResponseReceived={setAiResponse}
                onAiStatusChange={setAiStatus}
                onNewBatchId={setBootstrapBatchId}
              />
            )}

            {currentStage === "CONFIGURE" && (
              <ConfigureFlowShell
                tenantId={tenantId}
                tier={tier}
                bootstrapBatchId={bootstrapBatchId}
                aiResponse={aiResponse}
                aiStatus={aiStatus}
                onAiStatusChange={setAiStatus}
              />
            )}

            {currentStage === "SETUP" && (
              <DataSetupFlowShell
                tenantId={tenantId}
                tier={tier}
              />
            )}

            {currentStage === "EXECUTE" && (
              executeLoadError ? (
                <div role="alert" className="rounded-lg border border-[rgb(var(--color-danger))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  Unable to load execution context: {executeLoadError}
                </div>
              ) : executeFiscalPeriodId === null || executeScenarioSetId === null ? (
                <div className="px-2 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">Loading execution context…</div>
              ) : (
                <ExecuteFlowShell
                  tenantId={tenantId}
                  tier={tier}
                  fiscalPeriodId={executeFiscalPeriodId}
                  fiscalPeriodLabel={executeFiscalPeriodLabel}
                  scenarioSetId={executeScenarioSetId}
                />
              )
            )}

            {currentStage === "CERTIFY" && (
              // Stage 19 — CertificationPanel. Fetches its own period state internally.
              // period prop: null — shell does not own period; CertificationPanel fetches directly. S200.
              <CertificationPanel
                mode={mode}
                period={null}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
